(ns ui.view.grid-opensearch.view
  (:require [clojure.string :as string]
            [common.localization :as loc]
            [oops.core :as oops]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [ui.components.grid.view :as grid]
            [ui.dom-globals :as dg]
            [ui.view.grid-opensearch.columns-modal.view :as columns-modal]
            [ui.view.grid-opensearch.event :as event]
            [ui.view.grid-opensearch.selection :as selection]
            [ui.view.grid-opensearch.sub :as sub]
            [ui.view.grid-opensearch.theme :as theme]
            [ui.view.grid-opensearch.totals :as grid-totals]))

(defn get-main-menu-items
  [params-js]
  (let [os-field-keyword (-> (oops/ocall! params-js "column.getColId")
                             (keyword))
        column-id (oops/ocall! params-js "column.getColId")
        api (oops/oget params-js "api")
        on-clear-filter (fn []
                          (cond
                            (oops/oget+ api "setColumnFilterModel")
                            (-> (oops/ocall! api "setColumnFilterModel" column-id nil)
                                (js/Promise.resolve)
                                (.then (fn []
                                         (oops/ocall! api "onFilterChanged"))))

                            (oops/oget+ api "destroyFilter")
                            (do
                              (oops/ocall! api "destroyFilter" column-id)
                              (oops/ocall! api "onFilterChanged"))

                            :else
                            nil))

        menu-items [{:name   "Filter"
                     :action (fn [_]
                               (rf/dispatch [::event/add-filter os-field-keyword]))
                     :icon   "<i class=\"fal fa-filter\" aria-hidden=\"true\"></i>"}
                    {:name   (loc/tr :clear)
                     :action (fn [_]
                               (on-clear-filter))
                     :icon   "<i class=\"fal fa-times\" aria-hidden=\"true\"></i>"}
                    "separator"
                    "pinSubMenu"
                    "autoSizeThis"]]
    (clj->js menu-items)))

(defn- safe-oget
  [obj path]
  (try
    (oops/oget+ obj path)
    (catch :default _
      nil)))

(defn update-filter-model
  "Sends the column filters of the grid to the app state. When the owner of the
   grid gives `on-filter-model-changed`, it gets the model too, with string keys,
   as `event/apply-filter-model!` needs them."
  [{:keys [grid-id on-filter-model-changed]} params-js]
  (let [api (oops/oget+ params-js "api")
        filter-model-js (oops/ocall! api "getFilterModel")]
    (rf/dispatch [::event/filter-model-updated
                  grid-id
                  (js->clj filter-model-js :keywordize-keys true)])
    (when on-filter-model-changed
      (on-filter-model-changed (js->clj filter-model-js)))))

(defn- params->row-data
  [params-js]
  (or (safe-oget params-js "data")
      (safe-oget params-js "node.data")))

(defn- group-row-params?
  [params-js data]
  (let [node-group? (true? (oops/oget params-js "node.group"))
        has-child-count? (number? (get data :childCount))
        has-group-key? (contains? data :__group-key)]
    (or node-group? has-child-count? has-group-key?)))

(defn- empty-group-row?
  [data]
  (or (true? (aget data "__empty-group-row"))
      (true? (get data :__empty-group-row))))

(defn- handle-open-row
  [on-row-clicked params-js]
  (when (fn? on-row-clicked)
    (when-let [data (some-> (params->row-data params-js)
                            (js->clj :keywordize-keys true))]
      (when-not (or (group-row-params? params-js data)
                    (empty-group-row? data))
        (on-row-clicked data)))))

(defn- control-click?
  "True when the click of the row is on a control of the row: the selection checkbox, or a button.
   Such a click does its own work and does not open the preview of the row."
  [params-js]
  (boolean (some-> (safe-oget params-js "event")
                   dg/target
                   (dg/closest ".ag-selection-checkbox, .ag-checkbox, button, a"))))

(defn- row-entity-id
  "The id of the entity of the row of the AG Grid params, or nil for a group row."
  [params-js]
  (some-> (params->row-data params-js)
          (aget "original.db/id")))

(defn latest-callback
  "A function of one argument that calls the function that is in the atom `callback*` now, and does
   nothing when the atom has no function. The grid does not render again when only its callbacks
   change (see `render-affecting-props`), so a handler that AG Grid keeps must read the callback
   from an atom at the time of the click, and not hold the one that it got when it was made."
  [callback*]
  (fn [data]
    (let [callback @callback*]
      (when (fn? callback)
        (callback data)))))

(defn row-preview-click-fn
  "The handler of the click of a row of the grid. It calls `on-row-preview` with the data of the
   row, as a map with keyword keys. It does not call it for a click on a control of the row (the
   selection checkbox, a button or a link), for a group row, or when `on-row-preview` is not a
   function."
  [on-row-preview]
  (fn [params-js]
    (when-not (control-click? params-js)
      (handle-open-row on-row-preview params-js))))

(def ^:private active-row-class
  "The class of the row that the preview shows. `tailwind.css` has its look."
  "grid-opensearch-active-row")

(defn- group-row-label
  [params]
  (let [data (safe-oget params "data")
        label (or (safe-oget data "groupLabel")
                  (safe-oget params "value"))
        count (safe-oget data "childCount")]
    (cond
      (number? count) (str (or label "") " (" count ")")
      (some? label) label
      :else "")))

(defn- row-class
  [params]
  (let [data (params->row-data params)]
    (cond
      (and data
           (empty-group-row? data))
      "tw2-bg-table-bg-default tw2-text-tertiary"

      (and data
           (some? (aget data "__group-key")))
      "tw2-cursor-pointer tw2-bg-table-row-bg-hover tw2-font-semibold"

      :else
      "tw2-cursor-pointer")))

(def ^:private app-col-def-keys
  #{:currency-field-path
    :currency-flat-field
    :display-field
    :field-data-type
    :field-path
    :field-path-str
    :field-str
    :flat-field
    :label-field
    :name-column?
    :total-kind})

(def ^:private deprecated-col-def-keys
  #{:checkboxSelection
    :headerCheckboxSelection
    :suppressMenu})

(defn- total-cell-selector
  "The cell of the row of the totals and of a group row shows the total of the
   column, not a value of a row."
  [params-js]
  (when (grid-totals/total-row? params-js)
    #js {:component grid-totals/total-cell-renderer}))

(defn- ag-grid-col-def
  [col-def]
  (let [app-context (select-keys col-def app-col-def-keys)]
    (-> (apply dissoc
               (update col-def :context merge app-context)
               (into app-col-def-keys deprecated-col-def-keys))
        (assoc :cellRendererSelector total-cell-selector))))

(defn- normalize-field-path
  [field-path]
  (cond
    (vector? field-path) field-path
    (keyword? field-path) [field-path]
    (sequential? field-path) (vec field-path)
    :else nil))

(defn- active-group-by-field-path
  [api column-defs groupable-field-paths]
  (let [column-state (-> (oops/ocall! api "getColumnState")
                         (js->clj :keywordize-keys true))
        groupable-field-paths (set (keep normalize-field-path groupable-field-paths))
        grouped-columns (->> (or column-state [])
                             (filter (fn [{:keys [rowGroup]}]
                                       (true? rowGroup)))
                             (sort-by (fn [{:keys [rowGroupIndex]}]
                                        (or rowGroupIndex 0))))
        grouped-col-id (:colId (first grouped-columns))
        grouped-col-def (some (fn [col-def]
                                (when (= (:colId col-def) grouped-col-id)
                                  col-def))
                              column-defs)
        grouped-field-path (normalize-field-path (or (:field-path grouped-col-def)
                                                     (:field grouped-col-def)))]
    (when (contains? groupable-field-paths grouped-field-path)
      grouped-field-path)))

(defn- sync-group-by-from-grid!
  [params-js column-defs groupable-field-paths on-group-by-changed]
  (let [api (oops/oget+ params-js "api")
        group-by-field-path (active-group-by-field-path api column-defs groupable-field-paths)]
    (when (fn? on-group-by-changed)
      (on-group-by-changed group-by-field-path))
    group-by-field-path))

(defn- render-affecting-props
  [props]
  ;; The active entity changes only the highlight of the active row. A redraw of the rows updates
  ;; that highlight (see `should-component-update`), so exclude the active entity here. If it stayed,
  ;; a change of the preview would re-render the grid, and the re-render makes AG Grid refetch.
  ;; The callbacks get a new identity on each render of the parent. AG Grid keeps the handler that
  ;; it reads at construction, or the component reads the latest callback from an atom (see
  ;; `preview-callback*`), so a new identity of a callback must not render the grid.
  (dissoc props
          :accessible-field-paths
          :active-entity-id
          :external-filter-context
          :on-create-first-item
          :on-filter-model-changed
          :on-grid-ready
          :on-group-by-changed
          :on-row-clicked
          :on-row-preview
          :on-select-all-changed
          :on-selection-changed))

(defn view
  [{:keys [grid-id] :as props}]
  (let [latest-grid-state* (atom nil)
        active-entity-id* (atom nil)
        preview-callback* (atom nil)
        datasource* (atom nil)]
    (r/create-class
     {:component-did-mount
      (fn []
        (rf/dispatch [::event/init props]))

      :should-component-update
      (fn [_ [_ old-props] [_ new-props]]
        ;; A change of the callback of the preview alone does not render the grid, so the latest
        ;; callback is kept here for the handler of the click of a row.
        (reset! preview-callback* (:on-row-preview new-props))
        ;; A change of the active entity updates only the highlight of the active row. Keep the new
        ;; value for `rowClassRules`, and redraw the rows. Do not render the grid, because a
        ;; re-render makes AG Grid refetch.
        (when (not= (:active-entity-id new-props) (:active-entity-id old-props))
          (reset! active-entity-id* (:active-entity-id new-props))
          (rf/dispatch [::event/redraw-rows grid-id]))
        (not= (render-affecting-props old-props)
              (render-affecting-props new-props)))

      :component-did-update
      (fn [this [_ old-props]]
        (let [new-props (r/props this)
              new-filters (:external-filters-by-field-path new-props)
              old-filters (:external-filters-by-field-path old-props)
              new-context (:external-filter-context new-props)
              new-query-text (:external-query-text new-props)
              old-query-text (:external-query-text old-props)
              new-pipeline-id (:pipeline-id new-props)
              old-pipeline-id (:pipeline-id old-props)
              new-entity-type (:entity-type new-props)
              old-entity-type (:entity-type old-props)
              new-group-by-field (:group-by-field new-props)
              old-group-by-field (:group-by-field old-props)
              new-row-group-sort-mode (:row-group-sort-mode new-props)
              old-row-group-sort-mode (:row-group-sort-mode old-props)]
          (when (or (not= new-filters old-filters)
                    (not= new-query-text old-query-text))
            (rf/dispatch [::event/set-external-filters
                          grid-id
                          new-filters
                          new-context
                          new-query-text]))
          (when (or (not= new-pipeline-id old-pipeline-id)
                    (not= new-entity-type old-entity-type))
            (rf/dispatch [::event/init new-props])
            (rf/dispatch [::event/trigger-query-search grid-id]))
          (when (or (not= new-group-by-field old-group-by-field)
                    (not= new-row-group-sort-mode old-row-group-sort-mode))
            (rf/dispatch [::event/trigger-query-search grid-id]))))

      :component-will-unmount
      (fn []
        (rf/dispatch [::event/destroy grid-id]))

      :reagent-render
      (fn [{:keys [grid-id entity-type pipeline-id external-filters-by-field-path
                   external-query-text group-by-field row-group-sort-mode on-row-clicked on-row-preview
                   active-entity-id on-selection-changed
                   on-select-all-changed on-create-first-item
                   on-group-by-changed groupable-field-paths hide-empty-columns?
                   on-grid-ready on-filter-model-changed]
            :as _props}]
        (reset! active-entity-id* active-entity-id)
        (reset! preview-callback* on-row-preview)
        (let [column-defs @(rf/subscribe [::sub/ag-grid-col-defs grid-id])
              grid-theme (theme/grid-theme {:density           @(rf/subscribe [::sub/density grid-id])
                                            :show-entity-type? @(rf/subscribe [::sub/show-entity-type? grid-id])})
              groupable-field-paths (set (keep normalize-field-path groupable-field-paths))
              group-by-field (normalize-field-path group-by-field)
              grouped? (some? group-by-field)
              grouped-column-defs (mapv (fn [col-def]
                                          (let [field-path (normalize-field-path
                                                            (or (:field-path col-def)
                                                                (:field col-def)))
                                                can-group? (contains? groupable-field-paths field-path)
                                                grouped-col? (and grouped?
                                                                  can-group?
                                                                  (= field-path group-by-field))]
                                            (cond-> (assoc col-def
                                                           :rowGroup grouped-col?
                                                           :enableRowGroup can-group?)
                                              grouped-col? (assoc :hide true))))
                                        column-defs)
              fields @(rf/subscribe [::sub/fields grid-id])
              os-queries @(rf/subscribe [::sub/os-queries grid-id])
              filter-model @(rf/subscribe [::sub/filter-model grid-id])
              has-external-filters? (seq (or external-filters-by-field-path {}))
              has-grid-filters? (seq (or filter-model {}))
              has-query? (not (string/blank? (or external-query-text "")))
              filtered? (or has-external-filters?
                            has-grid-filters?
                            has-query?)]
          (reset! latest-grid-state*
                  {:column-defs grouped-column-defs
                   :entity-type entity-type
                   :fail-js nil
                   :fields fields
                   :filter-model filter-model
                   :grid-id grid-id
                   :grouped? grouped?
                   :hide-empty-columns? hide-empty-columns?
                   :os-queries os-queries
                   :pipeline-id pipeline-id
                   :row-group-sort-mode row-group-sort-mode})
          (when-not @datasource*
            (reset! datasource*
                    {:getRows (fn [params-js]
                                (let [{:keys [request success fail]} (js->clj params-js :keywordize-keys true)
                                      {:keys [startRow endRow filterModel sortModel rowGroupCols groupKeys]} request
                                      {:keys [column-defs entity-type fields grid-id hide-empty-columns?
                                              os-queries pipeline-id row-group-sort-mode]} @latest-grid-state*
                                      on-ok (fn [resp]
                                              (success (clj->js resp)))]
                                  (rf/dispatch [::event/load-data
                                                grid-id
                                                {:load-callback on-ok
                                                 :fail-js       fail
                                                 :entity-type   entity-type
                                                 :pipeline-id   pipeline-id
                                                 :os-fields     fields
                                                 :column-defs   column-defs
                                                 :os-queries    os-queries
                                                 :sort-model    sortModel
                                                 :filter-model  filterModel
                                                 :row-group-sort-mode row-group-sort-mode
                                                 :hide-empty-columns? hide-empty-columns?
                                                 :row-group-cols rowGroupCols
                                                 :group-keys    groupKeys
                                                 :start-row     startRow
                                                 :end-row       endRow}])))}))
          [:div
           {:class "flex flex-auto flex-col gap-2"}
           [:div.ag-theme-alpine.grid-opensearch-table
            {:style {:height "100%"}}
            (when (seq grouped-column-defs)
              [grid/ag-grid-js
               {:theme                        grid-theme
                :columnDefs                   (mapv ag-grid-col-def grouped-column-defs)
                :tooltipShowDelay             0
                :tooltipMouseTrack            true
                :defaultColDef                {:sortable              true
                                               :filter                true
                                               :resizable             true
                                               :enableCellChangeFlash true
                                               :editable              false
                                               :menuTabs              ["generalMenuTab" "filterMenuTab"]
                                               :suppressHeaderMenuButton false
                                               :suppressHeaderFilterButton false
                                               :suppressFloatingFilterButton false}
                :getMainMenuItems             get-main-menu-items
                :context                      {:entityType  entity-type
                                               :onOpenEntity on-row-clicked}
                :suppressMenuHide             false
                :rowBuffer                    10
                :onGridReady                  (fn [params-js]
                                                (let [api (oops/oget params-js "api")]
                                                  (rf/dispatch [::event/grid-ready grid-id api])
                                                  (when on-grid-ready
                                                    (on-grid-ready api))))
                :onRowClicked                 (row-preview-click-fn (latest-callback preview-callback*))
                :rowClassRules                {active-row-class (fn [params-js]
                                                                  (let [id (row-entity-id params-js)]
                                                                    (and (some? @active-entity-id*)
                                                                         (some? id)
                                                                         (= @active-entity-id* (js/parseInt id 10)))))}
                :onRowDoubleClicked           (fn [params-js]
                                                (handle-open-row on-row-clicked params-js))
                :onCellClicked                (fn [_params-js]
                                                nil)
                :onCellDoubleClicked          (fn [params-js]
                                                (handle-open-row on-row-clicked params-js))
                :suppressColumnVirtualisation true
                :suppressColumnMoveAnimation  true
                :pivotPanelShow               "never"
                :animateRows                  true

               ;; Column state updates
                :multiSortKey                 "shift"
                :onSortChanged                (fn [args]
                                                (rf/dispatch [::event/columns-changed grid-id args]))
                :onColumnMoved                (fn [args]
                                                (rf/dispatch [::event/columns-changed grid-id args]))
                :onColumnVisible              (fn [args]
                                                (rf/dispatch [::event/columns-changed grid-id args]))
                :onColumnPinned               (fn [args]
                                                (rf/dispatch [::event/columns-changed grid-id args]))
                ;; AG Grid sends this event while the user drags the edge of a column.
                ;; Keep the width only when the drag is finished. A width that the code
                ;; sets (source "api"), for example the reset of the grid, is not a
                ;; choice of the user, and is not kept.
                :onColumnResized              (fn [args]
                                                (when (and (oops/oget args "finished")
                                                           (not= "api" (oops/oget args "source")))
                                                  (rf/dispatch [::event/column-resized
                                                                grid-id
                                                                (-> (oops/ocall! (oops/oget args "api") "getColumnState")
                                                                    (js->clj :keywordize-keys true))])))
                :onColumnRowGroupChanged      (fn [args]
                                                (sync-group-by-from-grid! args column-defs groupable-field-paths on-group-by-changed)
                                                (rf/dispatch [::event/columns-changed grid-id args]))
                :onFilterChanged              (partial update-filter-model
                                                       {:grid-id                grid-id
                                                        :on-filter-model-changed on-filter-model-changed})
                ;; A group is a row of the group column, so that each number column
                ;; can show the total of the group under its header.
                :groupDisplayType             (if grouped? "singleColumn" "multipleColumns")
                :autoGroupColumnDef           (when grouped?
                                                {:headerName (loc/tr :group-by)
                                                 :minWidth 320
                                                 :pinned "left"
                                                 :cellRendererParams {:suppressCount true
                                                                      :innerRenderer group-row-label}})
                :getRowClass                  row-class
                :rowModelType                 "serverSide"
                :serverSideDatasource         @datasource*
                :isServerSideGroup            (fn [data-item]
                                                (number? (aget data-item "childCount")))
                :getServerSideGroupKey        (fn [data-item]
                                                (aget data-item "__group-key"))
                :cacheBlockSize               100
                :maxBlocksInCache             1
                :rowGroupPanelShow            "onlyWhenGrouping"
                ;; A grouped grid has no total and no query for a select-all, so a
                ;; bulk action could act on the wrong rows. It has no header checkbox.
                :rowSelection                 {:mode                   "multiRow"
                                               :checkboxes             true
                                               :headerCheckbox         (not grouped?)
                                               ;; A group row is not a record, so it has
                                               ;; no checkbox (see `isRowSelectable`).
                                               :hideDisabledCheckboxes true
                                               :enableClickSelection   false}
                ;; AG Grid's auto-generated selection checkbox column
                ;; otherwise renders unpinned wherever it lands in column
                ;; order (in practice: scrollable, not stuck to the left
                ;; alongside a pinned name column) - pin it explicitly so it
                ;; stays visible while horizontally scrolling.
                :selectionColumnDef           {:pinned "left"}
                :isRowSelectable              (fn [node]
                                                (and (not (oops/oget node "group"))
                                                     (not (empty-group-row? (oops/oget node "data")))))
                :onSelectionChanged           (fn [params-js]
                                                (let [{:keys [select-all? toggled-ids]}
                                                      (some-> (dg/ag-grid-server-side-selection-state params-js)
                                                              (js->clj)
                                                              (selection/parse-selection-state))]
                                                  (cond
                                                    ;; A grouped grid has no header checkbox, but the keyboard
                                                    ;; (Ctrl+A) still selects all. Such a selection has no total
                                                    ;; and no query, so undo it.
                                                    (and select-all?
                                                         (:grouped? @latest-grid-state*))
                                                    (oops/ocall! (oops/oget params-js "api") "deselectAll")

                                                    ;; AG Grid keeps a select-all as a state, not as rows,
                                                    ;; so getSelectedRows returns none of them.
                                                    (and select-all?
                                                         (fn? on-select-all-changed))
                                                    (on-select-all-changed {:excluded-ids toggled-ids})

                                                    (fn? on-selection-changed)
                                                    (let [api (oops/oget params-js "api")
                                                          selected-rows (-> (oops/ocall! api "getSelectedRows")
                                                                            (js->clj))
                                                          selected-ids (->> selected-rows
                                                                            (keep (fn [row]
                                                                                    (or (get row "original.db/id")
                                                                                        (get row :original.db/id)
                                                                                        (:db/id row))))
                                                                            (vec))]
                                                      (on-selection-changed selected-ids
                                                                            {:source (oops/oget params-js "source")})))))
                :noRowsOverlayComponent       (fn [_params-js]
                                                (r/as-element
                                                 ;; AG Grid's .ag-overlay chrome sets pointer-events: none
                                                 ;; on the whole overlay stack (it's normally just static
                                                 ;; "no rows"/"loading" text) - without this override the
                                                 ;; create-first-item button below is visible but every
                                                 ;; click passes through to the grid body underneath it.
                                                 [:div {:class "flex flex-col items-center gap-2 py-6 text-center pointer-events-auto"}
                                                  [:div {:class "text-sm text-base-light-text-paler"}
                                                   (if filtered?
                                                     (loc/tr :grid-no-entities-found)
                                                     (loc/tr :grid-no-entities-yet))]
                                                  (when filtered?
                                                    [:div {:class "text-xs text-base-light-text-paler"}
                                                     (loc/tr :new-grid-2023-no-entities-found-info)])
                                                  (when (and (not filtered?)
                                                             (fn? on-create-first-item))
                                                    [:button
                                                     {:class "mt-1 rounded border border-solid border-base-light-border px-3 py-1.5 text-sm hover:bg-base-light-hover"
                                                      :type "button"
                                                      :on-click (fn [_]
                                                                  (on-create-first-item))}
                                                     (loc/tr :grid-no-entities-create-button)])]))
                :getRowId                     (fn [params]
                                                (let [data (oops/oget params "data")
                                                      group-key (aget data "__group-key")
                                                      empty-group-key (aget data "__empty-group-key")
                                                      group? (some? group-key)
                                                      parent-keys (or (oops/oget params "parentKeys") #js [])
                                                      leaf-id (aget data "original.db/id")]
                                                  (if group?
                                                    (str "group:" (.join parent-keys "|") ":" group-key)
                                                    (if (some? empty-group-key)
                                                      empty-group-key
                                                      (str leaf-id)))))
                :readOnlyEdit                 true}])]
           [columns-modal/modal-view {:grid-id grid-id}]]))})))
